<script>
import constants from "@/libs/Constants";
import ApiConnection from "@/libs/ApiConnection";
import ModalWindow from "@/components/ModalWindow.vue";
import GroupErrorTranslation from "@/libs/locales/errors";
import {VisualizationForecastData} from "@/libs/VisualizationData";
import LineTimeSeries from "@/components/Charts/LineTimeSeries.vue";
import {
  getVisualizationComponentI18NLabel,
  UUIDfromList,
  downloadFileFromAElement,
  yearMonthStrToMonthYearI18N,
  disposeTooltipsByClass,
  setupTooltipsByClass
} from "@/libs/Helpers";
import OffCanvas from "@/components/OffCanvas.vue";

export default {
  name: 'PlotForecast',
  components: {
    OffCanvas,
    ModalWindow,
    LineTimeSeries
  },
  emits: ['onCloseModalClick', 'onOpenModal', 'afterPlotDataLoading', 'onTimeSeriesDataError'],
  props: {
    propFormData: VisualizationForecastData || null,
    propPlotLayers: Array || null,
    propPlotLayerNames: {type: Array, default: []},
    propPlotLayersCount: {type: Number, default: null},
    propPlotLayersTrigger: Boolean,
    propModalStatus: Boolean,
  },
  data() {
    return {
      constants: constants,
      plotData: null, //Data from API
      loading: {
        data: false, //Flag for data loading from API
        file: false,
      },
      offCanvasAreas: {
        show: false
      },
      lastUUID: null,
      modalStatus: false, //Status for modal to show
      plotDownload: false,
    }
  },
  watch: {
    propModalStatus(newValue, oldValue) {
      if (this.modalStatus !== newValue)
        this.modalStatus = newValue;
    },
    propPlotLayersTrigger(newValue, oldValue) {
      if (this.propFormData !== null && this.propPlotLayers !== null && this.propPlotLayers.length > 0) {
        /***
         * To avoid a request of data using the same parameters, the idea
         * is to build a UUID of the parameters (including the selected features)
         * and check whether the data is already present in the time-series or not.
         *
         * If the last UUID corresponds to the new one, then this method will only
         * send the signal to open the modal with data already loaded, otherwise
         * it makes the API request and sends the retrieved data.
         ***/
        let uuidList = [
          this.propFormData.component,
          this.propFormData.variable,
          this.propFormData.percentile,
          this.propFormData.creationYearMonthStr,
          this.propPlotLayers.sort().join(','),
          this.$i18n.locale // Forces data to reload when language changes outside the component
        ];

        let newUUID = UUIDfromList(uuidList);
        if (this.plotData === null || this.lastUUID === null || this.lastUUID !== newUUID) {
          this.loading.data = true;

          ApiConnection.postForecastTimeseries(
              this.propFormData.variable,
              this.propFormData.creationYearMonthStr,
              this.propPlotLayers
          ).then(response => {
            response.json().then(output => {
              //Update plot data
              this.plotData = output;

              //Update last UUID
              this.lastUUID = newUUID;

              //Open modal
              this.modalOpen();
            });
          }).catch((error) => {
            //Send error to parent
            this.$emit('onTimeSeriesDataError', GroupErrorTranslation.t(error.message, this.$i18n));
          }).finally(() => {
            //Unblock buttons
            this.loading.data = false;
            this.$emit('afterPlotDataLoading');
          });
        } else {
          this.$emit('afterPlotDataLoading');

          //Open modal
          this.modalOpen();
        }

        this.$nextTick(() => {
          this.tooltipsSetup();
          let object = document.getElementById('forecast-plot-offcanvas-link');
          if (object !== undefined && object !== null) {
            object.removeEventListener('click', this.offCanvasAreasShowOnClick);
            object.addEventListener('click', this.offCanvasAreasShowOnClick);
          }
        });
      }
    }
  },
  computed: {
    modalTitle() {
      let title = '';
      if (this.propFormData !== null) {
        let variableConfiguration = this.propFormData.variableConfiguration;
        title = variableConfiguration.getNameI18N(this.$i18n.locale)
      }
      return title
    },
    modalTitle2Objects() {
      let items = {};
      if (this.propFormData !== null)
        items[this.$t('InputSearchFormLabelTitle.CreationMonth')] = this.propFormData.creationMonthYearI18N(this.$i18n);
      return items;
    },
    formDataPercentile() {
      return this.propFormData !== null ? this.propFormData.percentile : null;
    },
    plotVariableNameUnit() {
      let title = this.modalTitle;

      let unit = '';
      if (this.propFormData !== null) {
        let variableConfiguration = this.propFormData.variableConfiguration;
        unit = variableConfiguration.unit;
      }

      if (unit !== '')
        title = `${title} [${unit}]`
      return title;
    },
    plotYAxis() {
      return this.plotVariableNameUnit;
    },
    plotTitle() {
      return this.plotVariableNameUnit;
    },
    plotFileName() {
      let name = 'forecast';
      if (this.propFormData !== null) {
        let variableConfiguration = this.propFormData.variableConfiguration;
        name = `${name}_${this.propFormData.variable}_${this.propFormData.creationYearMonthStr}`;
      }
      return name;
    },
    plotLabels() {
      let labels = [];
      if (this.plotData !== null)
        labels = this.plotData.labels !== undefined ? this.plotData.labels : []

      let labelsTransform = [];
      labels.forEach(value => {
        let newValue = yearMonthStrToMonthYearI18N(value, this.$i18n);

        // TODO: set label for selected forecast month in maps
        /*if (this.propFormData.forecastMonthYear === newValue)
          newValue = `${newValue} *`;*/

        labelsTransform.push(newValue);
      });

      return labelsTransform;
    },
    plotDatasets() {
      let datasets = [];
      let lineZero = [];

      if (this.propFormData !== null && this.plotData !== null && this.plotData !== undefined) {
        let plotFeatures = this.plotData.features;

        let colors = this.plotPercentileColorsGet();
        let colorRgb = colors.rgb;
        let colorRgbAlpha = colors.rgbAlpha;

        plotFeatures.below_mean.forEach((value) => {
          lineZero.push(0);
        })

        datasets = [
          {
            type: 'line',
            label: this.$t('InputSearchFormLabelValue.PercentileBelowAvg'), //Series name
            backgroundColor: colorRgbAlpha['below_mean'],
            borderColor: colorRgb['below_mean'],
            fill: {
              target: '+2', //Confidence interval between below mean and above mean
              below: 'rgba(140,140,162,0.3)'
            },
            tension: 0.2,
            pointRadius: 4,
            pointHoverRadius: 8,
            borderDash: this.formDataPercentile === constants.PERCENTILES.BELOW_MEAN ? [3, 3] : [],
            data: plotFeatures.below_mean !== undefined ? plotFeatures.below_mean : []
          },
          {
            type: 'line',
            label: this.$t('InputSearchFormLabelValue.PercentileOnAverage'), //Series name
            backgroundColor: colorRgbAlpha['mean'],
            borderColor: colorRgb['mean'],
            pointStyle: 'line',
            fill: false,
            tension: 0.2,
            borderWidth: 3,
            pointRadius: 4,
            pointHoverRadius: 8,
            borderDash: this.formDataPercentile === constants.PERCENTILES.MEAN ? [3, 3] : [],
            data: plotFeatures.mean !== undefined ? plotFeatures.mean : []
          },
          {
            type: 'line',
            label: this.$t('InputSearchFormLabelValue.PercentileAboveAvg'), //Series name
            backgroundColor: colorRgbAlpha['above_mean'],
            borderColor: colorRgb['above_mean'],
            fill: false,
            tension: 0.2,
            pointRadius: 4,
            pointHoverRadius: 8,
            borderDash: this.formDataPercentile === constants.PERCENTILES.ABOVE_MEAN ? [3, 3] : [],
            data: plotFeatures.above_mean !== undefined ? plotFeatures.above_mean : []
          }
        ];

        let historicalMeanData = plotFeatures.historic_mean;
        if (historicalMeanData !== undefined && historicalMeanData !== null && historicalMeanData.length > 0) {
          datasets.push({
                type: 'line',
                label: this.$t('InputSearchFormLabelValue.HistoricMean'), //Series name
                pointStyle: 'rectRot',
                backgroundColor: 'rgba(126, 26, 125, 0.5)',
                borderColor: 'rgb(126, 26, 125)',
                pointBorderWidth: 1,
                borderWidth: 0, // No line
                fill: false,
                tension: 0.2,
                pointRadius: 6,
                pointHoverRadius: 8,
                borderDash: [0, 0],
                data: historicalMeanData
              }
          )
        }
      }

      /**
       * Add zero-line to simulate annotation to fill plot with color above and beyond
       * The line will be then hidden from the tooltip and from the legend
       */
      if (this.propFormData !== null && this.propFormData.variableConfiguration !== null && lineZero.length > 0) {
        let backgroundColorAbove = this.propFormData.variableConfiguration.getBackgroundAboveRGBA(0.15);
        let backgroundColorBelow = this.propFormData.variableConfiguration.getBackgroundBelowRGBA(0.15);

        if (backgroundColorAbove !== null && backgroundColorBelow !== null) {
          datasets.push({
            type: 'line',
            data: lineZero,
            fill: {
              target: 'end',
              below: backgroundColorAbove, // Read from variable configuration
            },

            //Hidden line
            label: constants.LABELS.NO_LABEL,
            pointBorderWidth: 0,
            borderWidth: 0, // No line
            pointRadius: 0,
            pointHoverRadius: 0
          });

          datasets.push({
            type: 'line',
            data: lineZero,
            fill: {
              target: 'start',
              above: backgroundColorBelow // Read from variable configuration
            },
            showLine: false,
            hidden: false,
            //Hidden line
            label: constants.LABELS.NO_LABEL,
            pointBorderWidth: 0,
            borderWidth: 0,
            pointRadius: 0,
            pointHoverRadius: 0,
            borderColor: 'transparent'
          });
        }
      }

      return datasets;
    }
  },
  methods: {
    getVisualizationComponentI18NLabel,
    plotPercentileColorsGet() {
      let colorRgb = {};
      let colorRgbAlpha = {};

      let optionsDefault = {
        'below_mean': [224, 30, 30],
        'mean': [252, 191, 1],
        'above_mean': [42, 169, 233]
      };

      if (this.propFormData !== null) {
        let variableConfig = this.propFormData.variableConfiguration;

        let colorPalette = variableConfig.colors_percentile;
        Object.keys(optionsDefault).forEach(optionValue => {
          let colorRgbItems = optionsDefault[optionValue];

          let colorItem = colorPalette[optionValue];
          if (colorItem !== undefined && colorItem.length === 3)
            colorRgbItems = colorItem;

          let colorRgbStr = colorRgbItems.join(',');
          colorRgb[optionValue] = `rgba(${colorRgbStr},0.9)`;
          colorRgbAlpha[optionValue] = `rgba(${colorRgbStr},0.6)`;
        });
      }

      return {
        rgb: colorRgb,
        rgbAlpha: colorRgbAlpha,
      }
    },
    descriptionLabelI18NGet() {
      let params = {variable: this.modalTitle};

      let labelPostfix = 'area_one';
      let labelTooltipContent = '&nbsp;';

      if (this.propPlotLayers !== null && this.propPlotLayersCount !== null) {
        let selectedLayerCount = this.propPlotLayers.length;

        if (selectedLayerCount > 0 && this.propPlotLayersCount > 0) {
          if (selectedLayerCount === 1) {
            labelPostfix = 'area_one'

            if (this.propPlotLayerNames !== null) {
              labelTooltipContent = this.propPlotLayerNames.toString();
              labelTooltipContent = labelTooltipContent.replaceAll('"', "&#34;");
            }
          } else if (selectedLayerCount < this.propPlotLayersCount) {
            labelPostfix = 'area_many'
            params.area_count = selectedLayerCount;
          } else
            labelPostfix = 'area_all'
        }
      }

      params.tooltip_content = labelTooltipContent;
      return this.$t(`VisualizationPlot.forecast.${labelPostfix}`, params);
    },
    descriptionLongI18NGet() {
      let colors = this.plotPercentileColorsGet();
      let colorRgb = colors.rgbAlpha;

      let params = {};
      Object.keys(colorRgb).forEach(optionValue => {
        params[`color_${optionValue}`] = colorRgb[optionValue];
      });

      let labelOutput = this.$t('VisualizationPlot.forecast.lines', params);

      if (this.plotData !== null) {
        let plotFeatures = this.plotData.features;
        let historicalMeanData = plotFeatures.historic_mean;
        if (historicalMeanData !== undefined && historicalMeanData !== null && historicalMeanData.length > 0) {
          labelOutput = `${labelOutput} ${this.$t('VisualizationPlot.forecast.historic')}`;
        }
      }
      labelOutput = `${labelOutput} ${this.$t('VisualizationPlot.forecast.last')}`;

      return labelOutput;
    },
    modalOpen() {
      this.modalStatus = true;
      this.$emit('onOpenModal');
    },
    modalOnClose() {
      this.modalStatus = false;
      this.$emit('onCloseModalClick');
    },
    downloadImageButtonOnClick() {
      this.plotDownload = !this.plotDownload;
    },
    downloadFileButtonOnClick() {
      if (this.propFormData !== null && this.propPlotLayers !== null && this.propPlotLayers.length > 0) {
        this.loading.data = true;
        this.loading.file = true;

        ApiConnection.postForecastTimeseriesDownload(
            this.propFormData.variable,
            this.propFormData.creationYearMonthStr,
            this.propPlotLayers
        ).then(response => {
          let fileName = response.headers.get('filename');
          if (fileName === null)
            fileName = `${this.plotFileName}.csv`

          response.blob().then(data => {
            /*Create link with BLOB data to download*/
            downloadFileFromAElement(fileName, data);
          });
        }).catch((error) => {
          //Send error to parent
          this.$emit('onTimeSeriesDataError', GroupErrorTranslation.t(error.message, this.$i18n));
        }).finally(() => {
          //Unblock buttons
          this.loading.data = false;
          this.loading.file = false;
        });
      }
    },
    tooltipsDispose() {
      disposeTooltipsByClass('.tooltip-modal-init');
    },
    tooltipsSetup() {
      this.tooltipsDispose();
      setupTooltipsByClass('.tooltip-modal-init');
    },
    offCanvasAreasShowOnClick() {
      this.offCanvasAreas.show = true;
    },
    offCanvasAreasOnHidden() {
      this.offCanvasAreas.show = false;
    }
  }
}
</script>

<template>
  <ModalWindow prop-id="modalGraphForecast"
               prop-size="lg"
               :prop-static="true"
               :prop-status="this.modalStatus"
               @on-close-modal-click="modalOnClose">
    <template v-slot:title>
        <span class="h5 text-primary pe-1 fw-semibold">
          {{ $t(getVisualizationComponentI18NLabel(constants.COMPONENTS.FORECAST)) }}
        </span>{{ this.modalTitle }}
    </template>
    <template v-slot:subtitle>
      <template v-for="(value, name) in this.modalTitle2Objects">
        <span class="badge rounded-pill bg-secondary me-1">{{ name }}</span><span class="me-1">{{ value }}</span>
      </template>
    </template>
    <div class="row">
      <div class="col mb-1">
        <p class="fw-light lh-sm">
          <span v-html="descriptionLabelI18NGet()"></span>
          <br><br>
          <span v-html="descriptionLongI18NGet()"></span>
          <br><br>
          <span v-html="$t('VisualizationPlot.forecast.download')"></span>
        </p>
      </div>
    </div>
    <LineTimeSeries
        :prop-title="plotTitle !== undefined && plotTitle !== null ? plotTitle : ''"
        :prop-labels="this.plotLabels"
        :prop-datasets="this.plotDatasets"
        :prop-series="plotDatasets"
        :prop-download-toggle="plotDownload"
        :prop-download-file-name="plotFileName"
        :prop-x-axis-label="$t('InputSearchFormLabelTitle.ForecastMonth')"
        :prop-y-axis-label="this.plotYAxis">
    </LineTimeSeries>
    <template v-slot:footer>
      <div class="btn-group dropup">
        <button type="button" class="btn btn-success dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false"
                :disabled="loading.data === true">
          <div v-if="loading.file === true" class="spinner-grow spinner-grow-sm me-2" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <i class="bi bi-download pe-2" v-else></i>
          <span class="pe-1">{{ $t('buttons.downloadData') }}</span>
        </button>
        <ul class="dropdown-menu">
          <li>
            <button class="dropdown-item" role="link" @click="downloadImageButtonOnClick"
                    :disabled="loading.data === true">
              <i class="bi bi-image pe-2"></i>{{ $t('buttons.downloadDataImage') }}
            </button>
          </li>
          <li>
            <hr class="dropdown-divider">
          </li>
          <li>
            <button class="dropdown-item" role="link" @click="downloadFileButtonOnClick"
                    :disabled="loading.data === true">
              <i class="bi bi-file-earmark-spreadsheet pe-2"></i>{{ $t('buttons.downloadDataCSV') }}
            </button>
          </li>
        </ul>
      </div>
    </template>
    <OffCanvas
        prop-id="plotForecastOffCanvas"
        :prop-title="$tc('InputSearchFormLabelValue.Areas', propPlotLayerNames.length)"
        :prop-show="offCanvasAreas.show"
        prop-position="end"
        :prop-hide-close-button="false"
        @on-canvas-hidden="offCanvasAreasOnHidden">
      <table class="table table-sm table-hover table-bordered table-striped">
        <tbody>
        <tr v-for="(area, index) in propPlotLayerNames">
          <td class="fw-semibold text-end">{{ index + 1 }}</td>
          <td>{{ area }}</td>
        </tr>
        </tbody>
      </table>
    </OffCanvas>
  </ModalWindow>
</template>